// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/machine-group/MachineGroupDrawer.jsx
// 📝 Purpose:
//   Machine Group Master – Create / Edit Drawer
//   - group_code, group_name
//   - Lifecycle handled by backend (DRAFT / ACTIVE / INACTIVE)
//   - NO status_id from UI
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function MachineGroupDrawer({
  open,
  mode = "create", // create | edit
  record = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    group_code: "",
    group_name: "",
    is_active: true,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------
  // Initialize form
  // ------------------------------------------------------------
  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        group_code: record.group_code || "",
        group_name: record.group_name || "",
        is_active: record.is_active ?? true,
      });
    } else {
      setForm({
        group_code: "",
        group_name: "",
        is_active: true,
      });
    }
  }, [mode, record]);

  if (!open) return null;

  const onChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // ------------------------------------------------------------
  // Submit handler
  // ------------------------------------------------------------
  const onSubmit = async () => {
    setError(null);

    if (!form.group_code || !form.group_name) {
      setError("Group Code and Group Name are mandatory");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        group_code: form.group_code,
        group_name: form.group_name,
      };

      // is_active only allowed during edit
      if (mode === "edit") {
        payload.is_active = form.is_active;
      }

      if (mode === "create") {
        await axiosClient.post(
          "/api/ppe/masters/machine-groups",
          payload
        );
      }

      if (mode === "edit" && record?.id) {
        await axiosClient.put(
          `/api/ppe/masters/machine-groups/${record.id}`,
          payload
        );
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 420,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create"
            ? "Create Machine Group"
            : "Edit Machine Group"}
        </h6>
        <button className="btn btn-sm btn-light" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* ====================================================== */}
      {/* Body */}
      {/* ====================================================== */}
      <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <label className="form-label">Group Code *</label>
          <input
            className="form-control"
            value={form.group_code}
            onChange={(e) =>
              onChange("group_code", e.target.value)
            }
            disabled={mode === "edit"}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Group Name *</label>
          <input
            className="form-control"
            value={form.group_name}
            onChange={(e) =>
              onChange("group_name", e.target.value)
            }
          />
        </div>

        {mode === "edit" && (
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                onChange("is_active", e.target.checked)
              }
            />
            <label className="form-check-label">
              Active
            </label>
          </div>
        )}
      </div>

      {/* ====================================================== */}
      {/* Footer */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
